import React, { useState, useMemo } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { 
  Search, Plus, Check, ChevronRight, ChevronDown, ChevronUp, Layers, 
  GripVertical, Filter, X, Users 
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Componente reutilizável para seleção de operações
 * Usado nos modais de criação e edição de OFs
 * 
 * @param {Object} props
 * @param {Array} props.operacoes - Lista de todas as operações disponíveis
 * @param {Array} props.selectedOperacoes - Operações atualmente selecionadas
 * @param {Function} props.setSelectedOperacoes - Função para atualizar operações selecionadas
 * @param {Array} props.categorias - Lista de categorias (para ordenação e cores)
 * @param {Array} props.operadores - Lista de operadores disponíveis
 * @param {Array} props.tags - Lista de tags para filtrar operadores
 * @param {Array} props.modelos - Templates de operações (opcional)
 * @param {Function} props.onAplicarModelo - Callback para aplicar um modelo
 * @param {Function} props.filtrarOperadoresPorPermissao - Função para filtrar operadores por permissão
 * @param {Function} props.getOperacaoInfo - Função para obter info completa da operação
 */
export default function OperacoesSelector({
  operacoes = [],
  selectedOperacoes = [],
  setSelectedOperacoes,
  categorias = [],
  operadores = [],
  tags = [],
  modelos = [],
  onAplicarModelo,
  filtrarOperadoresPorPermissao,
  getOperacaoInfo
}) {
  // Estados locais
  const [searchOperacao, setSearchOperacao] = useState('');
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [filtroTagsOperadores, setFiltroTagsOperadores] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  // Estado para controlar quais operações têm a lista de operadores expandida
  const [expandedOperadores, setExpandedOperadores] = useState({});
  
  // Número de operadores a mostrar por defeito (quando colapsado) - mostra até preencher a linha
  const OPERADORES_VISIVEIS_DEFAULT = 6;

  // Categorias personalizadas (com operacoes_ids)
  const categoriasPersonalizadas = useMemo(() => 
    categorias.filter(cat => cat.operacoes_ids && cat.operacoes_ids.length > 0),
    [categorias]
  );

  // Agrupar operações por categoria
  const operacoesPorCategoria = useMemo(() => {
    const map = {};
    operacoes.forEach(op => {
      const cat = op.categoria || 'Sem Categoria';
      // Excluir operações sem categoria
      if (cat === 'Sem Categoria') return;
      if (!map[cat]) map[cat] = [];
      map[cat].push(op);
    });
    
    // Ordenar por ordem das categorias
    const ordenadas = categorias
      .filter(cat => map[cat.nome])
      .map(cat => ({ 
        catObj: cat, 
        nome: cat.nome, 
        ops: map[cat.nome],
        cor: cat.cor || '#6b7280'
      }));
    
    // NÃO adicionar categorias "Sem Categoria" - removido
    
    return ordenadas;
  }, [operacoes, categorias]);

  // Função para filtrar operações por pesquisa
  const filtrarOps = (ops) => {
    if (!searchOperacao) return ops;
    const term = searchOperacao.toLowerCase();
    return ops.filter(op => 
      op.codigo?.toLowerCase().includes(term) ||
      op.nome?.toLowerCase().includes(term)
    );
  };

  // Toggle operação (adicionar/remover)
  const handleToggleOperacao = (op) => {
    const exists = selectedOperacoes.find(sel => sel.operacao_id === op.id);
    
    if (exists) {
      // Remover
      setSelectedOperacoes(prev => prev.filter(sel => sel.operacao_id !== op.id));
    } else {
      // Adicionar - depois ordenar por categoria e ordem da operação
      const newOp = {
        operacao_id: op.id,
        codigo: op.codigo,
        nome: op.nome,
        categoria_id: op.categoria_id,
        id_temp: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        tempo_ajustado: op.tempo_standard || 30,
        operadores_atribuidos: [],
        ordem: selectedOperacoes.length + 1
      };
      
      setSelectedOperacoes(prev => {
        const newList = [...prev, newOp];
        
        // Ordenar a lista por: 1) ordem da categoria, 2) ordem da operação dentro da categoria
        newList.sort((a, b) => {
          const opA = operacoes.find(o => o.id === a.operacao_id);
          const opB = operacoes.find(o => o.id === b.operacao_id);
          
          if (!opA || !opB) return 0;
          
          // Obter índices das categorias
          const catIndexA = categorias.findIndex(c => c.nome === opA.categoria);
          const catIndexB = categorias.findIndex(c => c.nome === opB.categoria);
          
          // Categorias não encontradas vão para o fim
          const catA = catIndexA >= 0 ? catIndexA : 999;
          const catB = catIndexB >= 0 ? catIndexB : 999;
          
          // Primeiro comparar por categoria
          if (catA !== catB) {
            return catA - catB;
          }
          
          // Depois comparar por ordem da operação
          const ordemA = opA.ordem ?? 999;
          const ordemB = opB.ordem ?? 999;
          
          if (ordemA !== ordemB) {
            return ordemA - ordemB;
          }
          
          // Por fim, comparar por código alfabético
          return (opA.codigo || '').localeCompare(opB.codigo || '');
        });
        
        // Atualizar a ordem após ordenação
        return newList.map((o, idx) => ({ ...o, ordem: idx + 1 }));
      });
    }
  };

  // Adicionar operações de uma categoria personalizada
  const handleAddCategoria = (cat) => {
    const opsIds = cat.operacoes_ids || [];
    const opsToAdd = opsIds
      .map(opId => operacoes.find(o => o.id === opId))
      .filter(Boolean)
      .filter(op => !selectedOperacoes.some(sel => sel.operacao_id === op.id));
    
    if (opsToAdd.length > 0) {
      const newOps = opsToAdd.map((op, idx) => ({
        operacao_id: op.id,
        codigo: op.codigo,
        nome: op.nome,
        id_temp: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${idx}`,
        tempo_ajustado: op.tempo_standard || 30,
        operadores_atribuidos: [],
        ordem: selectedOperacoes.length + idx + 1
      }));
      setSelectedOperacoes(prev => [...prev, ...newOps]);
      toast.success(`${opsToAdd.length} operações adicionadas`);
    } else {
      toast.info('Todas já estão adicionadas');
    }
  };

  // Ajustar tempo da operação
  const handleAjustarTempo = (idTemp, delta) => {
    setSelectedOperacoes(prev => prev.map(op => 
      op.id_temp === idTemp 
        ? { ...op, tempo_ajustado: Math.max(1, (op.tempo_ajustado || 30) + delta) }
        : op
    ));
  };

  // Definir tempo diretamente
  const handleSetTempo = (idTemp, value) => {
    const tempo = parseInt(value) || 1;
    setSelectedOperacoes(prev => prev.map(op => 
      op.id_temp === idTemp ? { ...op, tempo_ajustado: Math.max(1, tempo) } : op
    ));
  };

  // Definir grupo
  const handleSetGrupo = (idTemp, grupo) => {
    setSelectedOperacoes(prev => {
      const updated = prev.map(op => 
        op.id_temp === idTemp ? { ...op, grupo: grupo || null } : op
      );
      
      // Se definiu um grupo, copiar operadores do outro membro do grupo
      if (grupo) {
        const outroDoGrupo = updated.find(o => o.grupo === grupo && o.id_temp !== idTemp);
        if (outroDoGrupo?.operadores_atribuidos?.length > 0) {
          return updated.map(o => 
            o.id_temp === idTemp 
              ? { ...o, operadores_atribuidos: [...outroDoGrupo.operadores_atribuidos] }
              : o
          );
        }
      }
      return updated;
    });
  };

  // Remover operação
  const handleRemoveOperacao = (idTemp) => {
    setSelectedOperacoes(prev => prev.filter(op => op.id_temp !== idTemp));
  };

  // Toggle operador atribuído
  const handleToggleOperador = (idTemp, operadorId) => {
    setSelectedOperacoes(prev => {
      return prev.map(op => {
        if (op.id_temp !== idTemp) return op;
        
        const operadores = op.operadores_atribuidos || [];
        const exists = operadores.includes(operadorId);
        
        const novosOperadores = exists
          ? operadores.filter(id => id !== operadorId)
          : [...operadores, operadorId];
        
        // Se está em grupo, atualizar todos do grupo
        if (op.grupo) {
          return { ...op, operadores_atribuidos: novosOperadores };
        }
        
        return { ...op, operadores_atribuidos: novosOperadores };
      }).map(op => {
        // Sincronizar operadores entre membros do mesmo grupo
        const original = prev.find(o => o.id_temp === idTemp);
        if (original?.grupo && op.grupo === original.grupo && op.id_temp !== idTemp) {
          const updated = prev.find(o => o.id_temp === idTemp);
          const novosOperadores = updated?.operadores_atribuidos || [];
          const exists = novosOperadores.includes(operadorId);
          return {
            ...op,
            operadores_atribuidos: exists
              ? [...(op.operadores_atribuidos || []), operadorId].filter((v, i, a) => a.indexOf(v) === i)
              : (op.operadores_atribuidos || []).filter(id => id !== operadorId)
          };
        }
        return op;
      });
    });
  };

  // Drag & Drop
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    
    setSelectedOperacoes(prev => {
      const newArray = [...prev];
      const [movedItem] = newArray.splice(draggedIndex, 1);
      newArray.splice(dropIndex, 0, movedItem);
      return newArray.map((op, idx) => ({ ...op, ordem: idx + 1 }));
    });
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Grupos disponíveis
  const gruposDisponiveis = ['A', 'B', 'C', 'D', 'E'];

  // Tempo total
  const tempoTotal = selectedOperacoes.reduce((acc, op) => acc + (op.tempo_ajustado || 0), 0);

  return (
    <div className="grid grid-cols-5 gap-4 flex-1 min-h-0 overflow-hidden">
      {/* Painel Esquerdo - Operações Disponíveis (2/5 do espaço) */}
      <div className="col-span-2 flex flex-col min-h-0 border-r pr-4">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-stone-700">
            Operações Disponíveis ({operacoes.length})
          </label>
          <span className="text-xs text-stone-500">
            {operacoes.filter(op => !selectedOperacoes.some(sel => sel.operacao_id === op.id)).length} disponíveis
          </span>
        </div>
        
        {/* Atalhos de Categorias Personalizadas */}
        {categoriasPersonalizadas.length > 0 && (
          <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded-lg flex-shrink-0">
            <p className="text-xs font-medium text-amber-700 mb-2">Adicionar por Categoria:</p>
            <div className="flex flex-wrap gap-1">
              {categoriasPersonalizadas.map((cat) => {
                const opsIds = cat.operacoes_ids || [];
                const numJaAdicionadas = opsIds.filter(id => 
                  selectedOperacoes.some(sel => sel.operacao_id === id)
                ).length;
                const todasAdicionadas = numJaAdicionadas === opsIds.length && opsIds.length > 0;
                
                return (
                  <button
                    key={`cat-shortcut-${cat.id}`}
                    type="button"
                    onClick={() => handleAddCategoria(cat)}
                    className={`px-2 py-1 text-xs rounded-full border transition-colors flex items-center gap-1 ${
                      todasAdicionadas ? 'bg-green-100 border-green-400 text-green-700' : 'hover:bg-amber-100'
                    }`}
                    style={{ 
                      borderColor: todasAdicionadas ? undefined : cat.cor, 
                      color: todasAdicionadas ? undefined : cat.cor 
                    }}
                  >
                    {todasAdicionadas ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                    {cat.nome} ({numJaAdicionadas}/{opsIds.length})
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Templates/Modelos */}
        {modelos.length > 0 && onAplicarModelo && (
          <div className="flex gap-2 mb-2 flex-shrink-0">
            <span className="text-xs text-stone-500 self-center">Templates:</span>
            {modelos.slice(0, 3).map(modelo => (
              <Button 
                key={modelo.id} 
                variant="outline" 
                size="sm" 
                onClick={() => onAplicarModelo(modelo)} 
                className="text-xs"
              >
                {modelo.nome}
              </Button>
            ))}
          </div>
        )}

        {/* Pesquisa */}
        <div className="relative mb-2 flex-shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input 
            placeholder="Pesquisar por código ou nome..." 
            value={searchOperacao}
            onChange={(e) => setSearchOperacao(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {/* Lista de Operações por Categoria */}
        <div className="border rounded-lg flex-1 overflow-y-auto">
          {operacoesPorCategoria.map(({ catObj, nome, ops, cor }) => {
            const opsFiltradas = filtrarOps(ops);
            if (opsFiltradas.length === 0) return null;
            
            const isExpanded = expandedCategories.includes(nome) || searchOperacao;
            const numSelected = opsFiltradas.filter(op => 
              selectedOperacoes.some(sel => sel.operacao_id === op.id)
            ).length;
            
            return (
              <div key={nome} className="border-b last:border-b-0">
                <button
                  type="button"
                  onClick={() => setExpandedCategories(prev => 
                    prev.includes(nome) 
                      ? prev.filter(c => c !== nome)
                      : [...prev, nome]
                  )}
                  className="w-full flex items-center justify-between p-2 hover:bg-stone-50 text-sm font-medium"
                  style={{ backgroundColor: `${cor}15`, borderLeft: `4px solid ${cor}` }}
                >
                  <div className="flex items-center gap-2">
                    <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                    <span className="capitalize" style={{ color: cor }}>{nome}</span>
                    <span className="text-xs text-stone-400">({opsFiltradas.length})</span>
                  </div>
                  {numSelected > 0 && (
                    <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                      {numSelected} sel.
                    </span>
                  )}
                </button>
                
                {isExpanded && (
                  <div className="p-1 space-y-1 max-h-48 overflow-y-auto bg-white">
                    {opsFiltradas.map((op) => {
                      const isSelected = selectedOperacoes.some(sel => sel.operacao_id === op.id);
                      return (
                        <button
                          key={op.id}
                          type="button"
                          onClick={() => handleToggleOperacao(op)}
                          className={`w-full flex items-center gap-2 p-2 rounded text-left text-sm transition-colors ${
                            isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-stone-50'
                          }`}
                          data-testid={`op-${op.codigo}`}
                        >
                          <span className="font-mono text-xs text-stone-400 w-14">{op.codigo}</span>
                          <span className={`flex-1 truncate ${op.is_contagem ? 'font-medium text-sky-700' : ''}`}>
                            {op.nome}
                            {op.is_contagem && (
                              <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded border border-sky-200" title="Operação de Contagem">
                                CONT
                              </span>
                            )}
                          </span>
                          <span className="text-xs text-stone-500">{op.tempo_standard}s</span>
                          {isSelected && <Check className="h-4 w-4" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Painel Direito - Operações Selecionadas (3/5 do espaço) */}
      <div className="col-span-3 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <label className="block text-sm font-medium text-stone-700">
            Sequência de Operações ({selectedOperacoes.length})
          </label>
          <span className="text-xs text-stone-500">
            Tempo total: {tempoTotal}s ({Math.round(tempoTotal / 60)}min)
          </span>
        </div>
        
        {/* Filtro de Tags */}
        {tags.length > 0 && (
          <div className="flex gap-1 flex-wrap mb-2 p-2 bg-stone-50 rounded-lg flex-shrink-0">
            <span className="text-xs text-stone-500 flex items-center gap-1 mr-1">
              <Filter className="h-3 w-3" />
              Filtrar operadores:
            </span>
            {tags.map(tag => (
              <button
                key={tag.id}
                type="button"
                onClick={() => {
                  setFiltroTagsOperadores(prev => 
                    prev.includes(tag.id) 
                      ? prev.filter(id => id !== tag.id)
                      : [...prev, tag.id]
                  );
                }}
                className={`text-xs px-2 py-0.5 rounded-full border transition-all ${
                  filtroTagsOperadores.includes(tag.id)
                    ? 'text-white border-transparent'
                    : 'text-stone-600 border-stone-300 hover:border-stone-400'
                }`}
                style={filtroTagsOperadores.includes(tag.id) ? { backgroundColor: tag.cor } : {}}
              >
                {tag.nome}
              </button>
            ))}
            {filtroTagsOperadores.length > 0 && (
              <button
                type="button"
                onClick={() => setFiltroTagsOperadores([])}
                className="text-xs text-stone-400 hover:text-stone-600 underline ml-2"
              >
                limpar
              </button>
            )}
          </div>
        )}
        
        {/* Instrução de reordenação */}
        {selectedOperacoes.length > 1 && (
          <p className="text-xs text-stone-400 mb-2 flex items-center gap-1 flex-shrink-0">
            <GripVertical className="h-3 w-3" />
            Arraste as operações para reordenar
          </p>
        )}
        
        {/* Lista de Operações Selecionadas */}
        <div className="border rounded-lg p-3 flex-1 overflow-y-auto space-y-2">
          {selectedOperacoes.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="h-12 w-12 mx-auto text-stone-300 mb-3" />
              <p className="text-stone-400 text-sm">
                Selecione operações à esquerda
              </p>
              <p className="text-stone-400 text-xs mt-1">
                ou carregue uma minuta pré-definida
              </p>
            </div>
          ) : (
            selectedOperacoes.map((opSel, idx) => {
              const opInfo = getOperacaoInfo ? getOperacaoInfo(opSel.operacao_id) : 
                operacoes.find(o => o.id === opSel.operacao_id);
              if (!opInfo) return null;
              
              const categoria = categorias.find(c => c.nome === opInfo?.categoria);
              const categoriaId = categoria?.id || opInfo?.categoria_id;
              const catCor = categoria?.cor || '#6b7280';
              
              // Filtrar operadores por permissão e tags
              const operadoresFiltrados = filtrarOperadoresPorPermissao 
                ? filtrarOperadoresPorPermissao(opSel.operacao_id, categoriaId)
                    .filter(opr => {
                      if (filtroTagsOperadores.length === 0) return true;
                      return filtroTagsOperadores.some(tagId => opr.tags?.includes(tagId));
                    })
                : operadores.filter(opr => {
                    if (filtroTagsOperadores.length === 0) return true;
                    return filtroTagsOperadores.some(tagId => opr.tags?.includes(tagId));
                  });
              
              const operadoresAttr = opSel.operadores_atribuidos || [];
              const tempo = opSel.tempo_ajustado || opInfo?.tempo_standard || 30;
              
              // Gerar cor de fundo suave baseada na cor da categoria
              const hexToRgba = (hex, alpha) => {
                const r = parseInt(hex.slice(1, 3), 16);
                const g = parseInt(hex.slice(3, 5), 16);
                const b = parseInt(hex.slice(5, 7), 16);
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
              };
              const bgColor = hexToRgba(catCor, 0.08);
              const borderColor = hexToRgba(catCor, 0.3);
              
              return (
                <div 
                  key={opSel.id_temp} 
                  className={`p-3 rounded-lg border transition-all ${
                    opSel.grupo 
                      ? 'border-emerald-300' 
                      : ''
                  } ${draggedIndex === idx ? 'opacity-50 scale-95' : ''}`}
                  style={{ 
                    backgroundColor: opSel.grupo ? undefined : bgColor,
                    borderColor: opSel.grupo ? '#10b981' : borderColor,
                    borderLeftColor: opSel.grupo ? '#10b981' : catCor, 
                    borderLeftWidth: '4px'
                  }}
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, idx)}
                  onDragEnd={handleDragEnd}
                  data-testid={`selected-op-${opSel.operacao_id}`}
                >
                  {/* Linha 1: Info + Tempo + Grupo + Remover */}
                  <div className="flex items-center gap-2 mb-2">
                    <GripVertical className="h-4 w-4 text-stone-300 cursor-grab flex-shrink-0" />
                    <span className="text-xs font-bold text-white bg-primary rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                      {idx + 1}
                    </span>
                    <span className="font-mono text-xs font-bold px-2 py-0.5 bg-primary/10 text-primary rounded">
                      {opInfo?.codigo}
                    </span>
                    <span className="text-sm font-medium flex-1 truncate">
                      {opInfo?.nome}
                      {opInfo?.is_contagem && (
                        <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded border border-sky-200" title="Operação de Contagem">
                          CONT
                        </span>
                      )}
                    </span>
                    
                    {/* Controle de Tempo */}
                    <div className="flex items-center gap-1 bg-stone-100 px-2 py-1 rounded border">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-8 p-0 text-xs hover:bg-red-50 hover:text-red-600" 
                        onClick={() => handleAjustarTempo(opSel.id_temp, -10)}
                      >
                        -10
                      </Button>
                      <input
                        type="number"
                        value={tempo}
                        onChange={(e) => handleSetTempo(opSel.id_temp, e.target.value)}
                        className="w-12 h-6 text-sm font-bold border rounded text-center"
                      />
                      <span className="text-xs text-stone-500">s</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-8 p-0 text-xs hover:bg-green-50 hover:text-green-600" 
                        onClick={() => handleAjustarTempo(opSel.id_temp, 10)}
                      >
                        +10
                      </Button>
                    </div>
                    
                    {/* Grupo */}
                    <select
                      value={opSel.grupo || ''}
                      onChange={(e) => handleSetGrupo(opSel.id_temp, e.target.value)}
                      className={`text-xs border rounded px-2 py-1 min-w-[85px] ${
                        opSel.grupo 
                          ? 'bg-emerald-100 border-emerald-400 text-emerald-700 font-semibold' 
                          : 'bg-white border-stone-200'
                      }`}
                      title="Agrupar operações (mesmo operador)"
                    >
                      <option value="">Sem grupo</option>
                      {gruposDisponiveis.map(g => (
                        <option key={g} value={g}>Grupo {g}</option>
                      ))}
                    </select>
                    
                    {/* Remover */}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 w-6 p-0 text-stone-400 hover:text-red-500 hover:bg-red-50"
                      onClick={() => handleRemoveOperacao(opSel.id_temp)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Linha 2: Operadores */}
                  <div className="flex items-center gap-1 flex-wrap pl-7">
                    <Users className="h-3 w-3 text-stone-400" />
                    {operadoresFiltrados.length === 0 ? (
                      <span className="text-xs text-stone-400 italic">
                        Nenhum operador disponível
                      </span>
                    ) : (
                      <>
                        {/* Mostrar operadores conforme estado expandido */}
                        {(() => {
                          const isExpanded = expandedOperadores[opSel.id_temp];
                          const operadoresVisiveis = isExpanded 
                            ? operadoresFiltrados 
                            : operadoresFiltrados.slice(0, OPERADORES_VISIVEIS_DEFAULT);
                          const temMais = operadoresFiltrados.length > OPERADORES_VISIVEIS_DEFAULT;
                          
                          return (
                            <>
                              {operadoresVisiveis.map(opr => {
                                const isAttr = operadoresAttr.includes(opr.id);
                                // Formatar nome: primeiro e último nome
                                const nomeCompleto = opr.nome_completo || opr.nome || '';
                                const partes = nomeCompleto.trim().split(' ').filter(Boolean);
                                const nomeFormatado = partes.length >= 2 
                                  ? `${partes[0]} ${partes[partes.length - 1]}`
                                  : partes[0] || opr.username;
                                return (
                                  <button
                                    key={opr.id}
                                    type="button"
                                    onClick={() => handleToggleOperador(opSel.id_temp, opr.id)}
                                    className={`px-2.5 py-1 text-sm rounded-full border transition-all ${
                                      isAttr 
                                        ? 'text-white' 
                                        : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'
                                    }`}
                                    style={isAttr ? { 
                                      backgroundColor: catCor, 
                                      borderColor: catCor 
                                    } : undefined}
                                  >
                                    {nomeFormatado}
                                  </button>
                                );
                              })}
                              
                              {/* Botão Ver mais/menos */}
                              {temMais && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedOperadores(prev => ({
                                    ...prev,
                                    [opSel.id_temp]: !prev[opSel.id_temp]
                                  }))}
                                  className="flex items-center gap-0.5 px-2 py-1 text-sm transition-colors"
                                  style={{ color: catCor }}
                                >
                                  {isExpanded ? (
                                    <>
                                      <ChevronUp className="h-3.5 w-3.5" />
                                      ver menos
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="h-3.5 w-3.5" />
                                      +{operadoresFiltrados.length - OPERADORES_VISIVEIS_DEFAULT} mais
                                    </>
                                  )}
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        
        {/* Legenda de Grupos */}
        {selectedOperacoes.some(op => op.grupo) && (
          <div className="mt-2 p-2 bg-stone-50 rounded-lg flex-shrink-0">
            <p className="text-xs text-stone-500">
              <span className="font-medium">Grupagens:</span> Operações no mesmo grupo partilham operadores
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
